import React, { useState, useEffect } from 'react';
import { Box, Container, Card, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Button, CircularProgress, Typography, Chip, TextField, MenuItem, Grid, Select, FormControl, InputLabel, IconButton } from '@mui/material';
import { donationService } from '../services/api';
import { GetApp, TrendingUp, Preview, Search, Edit, Delete, People, TrendingDown, Repeat } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';

const sortToApi = (sortBy) => {
    const map = { 'date-desc': 'created_at-desc', 'date-asc': 'created_at-asc', 'amount-desc': 'amount-desc', 'amount-asc': 'amount-asc', 'name-asc': 'name-asc', 'name-desc': 'name-desc' };
    return map[sortBy] || 'created_at-desc';
  };

  const DonationsPage = () => {
  const navigate = useNavigate();
  const [donations, setDonations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterPaymentMethod, setFilterPaymentMethod] = useState('all');
  const [filterRecurring, setFilterRecurring] = useState('all');
  const [sortBy, setSortBy] = useState('date-desc');
  const [pageNumber, setPageNumber] = useState(1);
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalItems: 0,
    itemsPerPage: 10,
  });

  const fetchDonations = React.useCallback(async () => {
    try {
      setLoading(true);
      const params = {
        page: pageNumber,
        limit: 10,
        sort: sortToApi(sortBy),
      };
      if (searchQuery.trim()) params.search = searchQuery.trim();
      if (filterStatus && filterStatus !== 'all') params.payment_status = filterStatus;
      if (filterPaymentMethod && filterPaymentMethod !== 'all') params.payment_method = filterPaymentMethod;
      if (filterRecurring === 'recurring') params.is_recurring = 'true';
      else if (filterRecurring === 'one-time') params.is_recurring = 'false';
      const response = await donationService.getAllDonations(params);
      const data = response.data?.data ?? [];
      const pag = response.data?.pagination ?? { currentPage: 1, totalPages: 1, totalItems: 0, itemsPerPage: 10 };
      setDonations(data);
      setPagination(pag);
    } catch (error) {
      console.error('Error fetching donations:', error);
      setDonations([]);
    } finally {
      setLoading(false);
    }
  }, [pageNumber, searchQuery, filterStatus, filterPaymentMethod, filterRecurring, sortBy]);

  useEffect(() => {
    fetchDonations();
  }, [fetchDonations]);

  const downloadCSV = () => {
    const rows = [
      ['ID', 'Donor Name', 'Email', 'Amount', 'Campaign', 'Status', 'Date'],
      ...donations.map(d => [
        d.id,
        d.name,
        d.email,
        d.amount,
        d.campaign_title || 'General',
        d.payment_status,
        new Date(d.created_at).toLocaleDateString()
      ])
    ];
    const csv = rows.map(row => row.join(',')).join('\n');
    const a = document.createElement('a');
    a.href = 'data:text/plain;charset=utf-8,' + encodeURIComponent(csv);
    a.download = `donations_${Date.now()}.csv`;
    a.style.display = 'none';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  const totalPages = pagination.totalPages || 1;
  const paginatedDonations = donations;

  if (loading) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}><CircularProgress /></Box>;
  }

  const getStatusColor = (status) => {
    switch(status) {
      case 'completed': return 'success';
      case 'pending': return 'warning';
      case 'failed': return 'error';
      default: return 'default';
    }
  };

  const pageTotalAmount = donations
    .filter(d => d.payment_status === 'completed')
    .reduce((sum, d) => sum + (parseInt(d.amount, 10) || 0), 0);
  const totalDonors = pagination.totalItems;
  const averageDonation = donations.length > 0 ? Math.round(pageTotalAmount / donations.length) : 0;

  return (
    <Box sx={{ backgroundColor: '#f5f7fa', minHeight: '100%', p: 4 }}>
      <Container maxWidth={false} sx={{ maxWidth: '100%' }}>
        <Box sx={{ mb: 4 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
            <Box>
              <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#1a202c', mb: 1 }}>
                Donations
              </Typography>
              <Typography variant="body2" sx={{ color: '#667085' }}>
                Track and manage all donations ({pagination.totalItems} total)
              </Typography>
            </Box>
            <Button 
              variant="contained" 
              startIcon={<GetApp />}
              onClick={downloadCSV}
              sx={{ backgroundColor: '#2c5f2d', '&:hover': { backgroundColor: '#1f4620' } }}
            >
              Download CSV
            </Button>
          </Box>
        </Box>

        {/* Stats Summary */}
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: '1fr 1fr 1fr' }, gap: 3, mb: 4 }}>
            {/* Total Donations Received Card */}
            <Card sx={{ 
              p: 3,
              background: 'linear-gradient(135deg, #ffffff 0%, #f8fafb 100%)',
              border: '1px solid #e8ecf1',
              boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
              transition: 'all 0.3s ease-in-out',
              '&:hover': {
                boxShadow: '0 8px 16px rgba(0,0,0,0.12)',
                transform: 'translateY(-2px)'
              }
            }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Box sx={{ backgroundColor: '#dcfce7', p: 2, borderRadius: '12px' }}>
                  <TrendingUp sx={{ color: '#2c5f2d', fontSize: 28 }} />
                </Box>
                <Box sx={{ flex: 1 }}>
                  <Typography variant="body2" sx={{ color: '#667085', fontWeight: '500' }}>
                    Total Donations Received
                  </Typography>
                  <Typography variant="h5" sx={{ fontWeight: 'bold', color: '#2c5f2d', mt: 0.5 }}>
                    ₹{pageTotalAmount.toLocaleString()}
                  </Typography>
                  <Typography variant="caption" sx={{ color: '#999', display: 'block', mt: 0.5 }}>
                    Sum of completed on this page
                  </Typography>
                </Box>
              </Box>
            </Card>

            {/* Total Donors Card */}
            <Card sx={{ 
              p: 3,
              background: 'linear-gradient(135deg, #ffffff 0%, #f8fafb 100%)',
              border: '1px solid #e8ecf1',
              boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
              transition: 'all 0.3s ease-in-out',
              '&:hover': {
                boxShadow: '0 8px 16px rgba(0,0,0,0.12)',
                transform: 'translateY(-2px)'
              }
            }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Box sx={{ backgroundColor: '#e0e7ff', p: 2, borderRadius: '12px' }}>
                  <People sx={{ color: '#4f46e5', fontSize: 28 }} />
                </Box>
                <Box sx={{ flex: 1 }}>
                  <Typography variant="body2" sx={{ color: '#667085', fontWeight: '500' }}>
                    Total Donors
                  </Typography>
                  <Typography variant="h5" sx={{ fontWeight: 'bold', color: '#4f46e5', mt: 0.5 }}>
                    {totalDonors}
                  </Typography>
                  <Typography variant="caption" sx={{ color: '#999', display: 'block', mt: 0.5 }}>
                    {pagination.totalItems} donations (filtered)
                  </Typography>
                </Box>
              </Box>
            </Card>

            {/* Average Donation Card */}
            <Card sx={{ 
              p: 3,
              background: 'linear-gradient(135deg, #ffffff 0%, #f8fafb 100%)',
              border: '1px solid #e8ecf1',
              boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
              transition: 'all 0.3s ease-in-out',
              '&:hover': {
                boxShadow: '0 8px 16px rgba(0,0,0,0.12)',
                transform: 'translateY(-2px)'
              }
            }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Box sx={{ backgroundColor: '#fef3c7', p: 2, borderRadius: '12px' }}>
                  <TrendingDown sx={{ color: '#d97706', fontSize: 28 }} />
                </Box>
                <Box sx={{ flex: 1 }}>
                  <Typography variant="body2" sx={{ color: '#667085', fontWeight: '500' }}>
                    Average Donation
                  </Typography>
                  <Typography variant="h5" sx={{ fontWeight: 'bold', color: '#d97706', mt: 0.5 }}>
                    ₹{averageDonation.toLocaleString()}
                  </Typography>
                  <Typography variant="caption" sx={{ color: '#999', display: 'block', mt: 0.5 }}>
                    Per donor average
                  </Typography>
                </Box>
              </Box>
            </Card>
        </Box>

        {/* Search and Filter Section */}
        <Card sx={{ p: 3, mb: 3, border: '1px solid #e8ecf1', boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }}>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                fullWidth
                placeholder="Search by name, email, ID..."
                variant="outlined"
                size="small"
                value={searchQuery}
                onChange={(e) => { setSearchQuery(e.target.value); setPageNumber(1); }}
                InputProps={{
                  startAdornment: <Search sx={{ mr: 1, color: '#667085' }} />,
                }}
                sx={{
                  backgroundColor: '#fff',
                  '& .MuiOutlinedInput-root': {
                    '& fieldset': { borderColor: '#e8ecf1' }
                  }
                }}
              />
            </Grid>

            <Grid item xs={12} sm={6} md={2.5}>
              <FormControl fullWidth size="small">
                <InputLabel>Status</InputLabel>
                <Select
                  value={filterStatus}
                  label="Status"
                  onChange={(e) => { setFilterStatus(e.target.value); setPageNumber(1); }}
                  sx={{ backgroundColor: '#fff' }}
                >
                  <MenuItem value="all">All Status</MenuItem>
                  <MenuItem value="completed">Completed</MenuItem>
                  <MenuItem value="pending">Pending</MenuItem>
                  <MenuItem value="failed">Failed</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={6} md={2.5}>
              <FormControl fullWidth size="small">
                <InputLabel>Payment Method</InputLabel>
                <Select
                  value={filterPaymentMethod}
                  label="Payment Method"
                  onChange={(e) => { setFilterPaymentMethod(e.target.value); setPageNumber(1); }}
                  sx={{ backgroundColor: '#fff' }}
                >
                  <MenuItem value="all">All Methods</MenuItem>
                  <MenuItem value="card">Card</MenuItem>
                  <MenuItem value="upi">UPI</MenuItem>
                  <MenuItem value="netbanking">Net Banking</MenuItem>
                  <MenuItem value="wallet">Wallet</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={6} md={2}>
              <FormControl fullWidth size="small">
                <InputLabel>Recurring</InputLabel>
                <Select
                  value={filterRecurring}
                  label="Recurring"
                  onChange={(e) => { setFilterRecurring(e.target.value); setPageNumber(1); }}
                  sx={{ backgroundColor: '#fff' }}
                >
                  <MenuItem value="all">All Donations</MenuItem>
                  <MenuItem value="recurring">Recurring Only</MenuItem>
                  <MenuItem value="one-time">One-time Only</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={6} md={2}>
              <FormControl fullWidth size="small">
                <InputLabel>Sort By</InputLabel>
                <Select
                  value={sortBy}
                  label="Sort By"
                  onChange={(e) => { setSortBy(e.target.value); setPageNumber(1); }}
                  sx={{ backgroundColor: '#fff' }}
                >
                  <MenuItem value="date-desc">Newest First</MenuItem>
                  <MenuItem value="date-asc">Oldest First</MenuItem>
                  <MenuItem value="amount-desc">Highest Amount</MenuItem>
                  <MenuItem value="amount-asc">Lowest Amount</MenuItem>
                  <MenuItem value="name-asc">Name A-Z</MenuItem>
                  <MenuItem value="name-desc">Name Z-A</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </Card>

        <Card sx={{
          border: '1px solid #e8ecf1',
          boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
        }}>
          <TableContainer>
            <Table>
              <TableHead sx={{ backgroundColor: '#f8fafb', borderBottom: '2px solid #e8ecf1' }}>
                <TableRow>
                  <TableCell sx={{ fontWeight: '600', color: '#1a202c', py: 2 }}>ID</TableCell>
                  <TableCell sx={{ fontWeight: '600', color: '#1a202c' }}>Donor Name</TableCell>
                  <TableCell sx={{ fontWeight: '600', color: '#1a202c' }}>Email</TableCell>
                  <TableCell sx={{ fontWeight: '600', color: '#1a202c' }}>Amount</TableCell>
                  <TableCell sx={{ fontWeight: '600', color: '#1a202c' }}>Campaign</TableCell>
                  <TableCell sx={{ fontWeight: '600', color: '#1a202c' }}>Type</TableCell>
                  <TableCell sx={{ fontWeight: '600', color: '#1a202c' }}>Status</TableCell>
                  <TableCell sx={{ fontWeight: '600', color: '#1a202c' }}>Date</TableCell>
                  <TableCell sx={{ fontWeight: '600', color: '#1a202c' }}>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {paginatedDonations && paginatedDonations.length > 0 ? (
                  paginatedDonations.map(donation => (
                    <TableRow 
                      key={donation.id}
                      sx={{ 
                        '&:hover': { backgroundColor: '#f8fafb' },
                        borderBottom: '1px solid #e8ecf1'
                      }}
                    >
                      <TableCell sx={{ color: '#667085' }}>{donation.id}</TableCell>
                      <TableCell sx={{ fontWeight: '500', color: '#1a202c' }}>{donation.name}</TableCell>
                      <TableCell sx={{ color: '#667085' }}>{donation.email}</TableCell>
                      <TableCell sx={{ fontWeight: '500', color: '#059669' }}>₹{donation.amount.toLocaleString()}</TableCell>
                      <TableCell sx={{ color: '#667085' }}>{donation.campaign_title || 'General'}</TableCell>
                      <TableCell>
                        {(donation.is_recurring === true || donation.is_recurring === 1) ? (
                          <Chip 
                            icon={<Repeat />}
                            label={
                              donation.subscription_status
                                ? `${donation.recurring_frequency || 'Recurring'} · ${donation.subscription_status}`
                                : (donation.recurring_frequency
                                    ? donation.recurring_frequency.charAt(0).toUpperCase() + donation.recurring_frequency.slice(1)
                                    : 'Recurring')
                            }
                            color="primary"
                            variant="outlined"
                            size="small"
                            sx={{ fontWeight: '500' }}
                          />
                        ) : (
                          <Chip 
                            label="One-time"
                            variant="outlined"
                            size="small"
                            sx={{ fontWeight: '500', color: '#667085' }}
                          />
                        )}
                      </TableCell>
                      <TableCell>
                        <Chip 
                          label={donation.payment_status}
                          color={getStatusColor(donation.payment_status)}
                          variant="filled"
                          size="small"
                          sx={{ fontWeight: '500' }}
                        />
                      </TableCell>
                      <TableCell sx={{ color: '#667085' }}>{new Date(donation.created_at).toLocaleDateString()}</TableCell>
                      <TableCell sx={{ display: 'flex', gap: 1 }}>
                        <Button 
                          size="small"
                          startIcon={<Preview />}
                          onClick={() => navigate(`/admin/donations/${donation.id}`)}
                          sx={{ color: '#059669', mr: 1, textTransform: 'none' }}
                        >
                          View
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={9} align="center" sx={{ py: 4, color: '#667085' }}>
                      No donations found matching your criteria
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>

          {/* Pagination Controls */}
          <Box sx={{ p: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center', backgroundColor: '#f9fafb', borderTop: '1px solid #e8ecf1' }}>
            <Typography variant="body2" sx={{ color: '#667085' }}>
              Showing {paginatedDonations.length > 0 ? (pagination.currentPage - 1) * pagination.itemsPerPage + 1 : 0} to {(pagination.currentPage - 1) * pagination.itemsPerPage + paginatedDonations.length} of {pagination.totalItems} donations
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
              <Button
                variant="outlined"
                size="small"
                disabled={pageNumber === 1}
                onClick={() => setPageNumber(pageNumber - 1)}
              >
                Previous
              </Button>
              {[...Array(totalPages)].map((_, i) => (
                <Button
                  key={i + 1}
                  variant={pageNumber === i + 1 ? 'contained' : 'outlined'}
                  size="small"
                  onClick={() => setPageNumber(i + 1)}
                  sx={{
                    minWidth: '36px',
                    backgroundColor: pageNumber === i + 1 ? '#059669' : 'transparent',
                    color: pageNumber === i + 1 ? 'white' : '#667085',
                    '&:hover': {
                      backgroundColor: pageNumber === i + 1 ? '#047857' : '#f3f4f6'
                    }
                  }}
                >
                  {i + 1}
                </Button>
              ))}
              <Button
                variant="outlined"
                size="small"
                disabled={pageNumber === totalPages || totalPages === 0}
                onClick={() => setPageNumber(pageNumber + 1)}
              >
                Next
              </Button>
            </Box>
          </Box>
        </Card>
      </Container>
    </Box>
  );
};

export default DonationsPage;
